import {Empty, Select, Spin, Tooltip} from 'antd';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useEffect, useState} from 'react';

import {useInfinite} from '@/utils/createInfiniteHook';

const {Option} = Select;
const SelectElementComponent = ({
  queryKey,
  service,
  value,
  onChange,
  ignoreValues,
  includeValue,
  nameToShow,
  customMethod,
  filters,
  afterLoadData,
  additionalParams,
  customItemRender,
  customMapItem,
  sort,
  queryConfig,
  ...props
},) => {
  const name = nameToShow ? nameToShow : 'name';
  const [search, setSearch] = useState('');
  const [lastOptions, setLastOptions] = useState([]);
  const [open, setOpen] = useState(false);
  
  const onSearch = useCallback((val) => setSearch(val), []);
  
  let {isLoading, isFetching, data, fetchNextPage, hasNextPage} = useInfinite({
    key: [queryKey, filters, search],
    search,
    filters,
    service: customMethod || service?.search,
    additionalParams,
    sort: sort || {[name]: 1},
    queryConfig: {size: 50, enabled: open, ...queryConfig}
  });
  
  const handleDropdownVisibleChange = useCallback((visible) => {
    setOpen(visible);
  }, []);
  
  useEffect(() => {
    if (includeValue) {
      const exist = data && data.some((e) => e._id === includeValue?.id);
      if (!exist && typeof includeValue !== 'string')
        data.unshift(includeValue);
    }
    if (afterLoadData)
      afterLoadData(data);
    return data.map(e => e);
  }, [afterLoadData, data, includeValue]);
  
  const onScroll = useCallback((event) => {
    const target = event.target;
    if (!(isLoading && isFetching) && target.scrollTop + target.offsetHeight >= (target.scrollHeight - 50) && hasNextPage) {
      fetchNextPage();
    }
  }, [isLoading, isFetching, hasNextPage, fetchNextPage]);
  
  useEffect(() => {
    if (data) {
      if (ignoreValues) {
        if (Array.isArray(ignoreValues)) {
          if (ignoreValues?.length) {
            // eslint-disable-next-line react-hooks/exhaustive-deps,no-const-assign
            data = data.filter(elem => {
              const exist = ignoreValues?.some(e => {
                const toCheck = e?.id || e;
                return elem.id === toCheck;
              });
              return !exist;
            });
          }
        } else {
          // eslint-disable-next-line react-hooks/exhaustive-deps,no-const-assign
          data = data.filter(elem => elem.id !== ignoreValues);
        }
      }
      const opts = data.map(customMapItem).map(item => {
        return {...item, value: item.id, title: item[name], key: item.id, label: item.name || item.title};
      });
      setLastOptions(opts);
      return opts;
    }
    setLastOptions(lastOptions);
    return [];
  }, [data, ignoreValues, isLoading, setLastOptions]);
  
  const handlerBuildOptions = (item) => {
    return <Option key={item.value} value={item.value} title={item.title} {...item}>
      {customItemRender ? customItemRender(item, value, item.value === value) : <Tooltip
        title={item.label || item.title}>{item.label || item.title}</Tooltip>}
    </Option>;
  };
  
  const onClearHandler = useCallback(() => {
    setSearch('');
    onChange && onChange(null);
  }, [onChange]);
  
  const onChangeValueHandler = useCallback((e) => {
    // setSearch('');
    onChange && onChange(e);
  }, [onChange]);
  
  return (<Select
    optionFilterProp={'title'}
    showSearch
    filterOption={true}
    allowClear={!!value}
    {...props}
    onSearch={debounce(onSearch, 250)}
    loading={isLoading || isFetching}
    notFoundContent={isLoading || isFetching ? <div className="flex items-center justify-center"><Spin size="small"/>
    </div> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}/>}
    value={value}
    onChange={onChangeValueHandler}
    onDropdownVisibleChange={handleDropdownVisibleChange}
    onClear={onClearHandler}
    onPopupScroll={onScroll}
    className="w-full"
    style={{minWidth: '200px'}}
  >
    {!isFetching ? lastOptions.map((item) => handlerBuildOptions(item)) : [
      ...lastOptions.map((item) => handlerBuildOptions(item)),
      <Option key="loading" value="">
        <div key="loading" className="flex items-center justify-center w-full">
          <spa className="mr-4"><Spin size="small"/></spa>
          Loading...
        </div>
      </Option>
    ]}
  </Select>);
  
};

export default memo(SelectElementComponent);

SelectElementComponent.propTypes = {
  additionalParams: PropTypes.object,
  customMethod: PropTypes.func,
  filters: PropTypes.object,
  ignoreValues: PropTypes.any,
  includeValue: PropTypes.any,
  mode: PropTypes.string,
  nameToShow: PropTypes.string,
  onChange: PropTypes.func,
  customMapItem: PropTypes.func,
  sort: PropTypes.object,
  queryConfig: PropTypes.object,
  afterLoadData: PropTypes.func,
  customItemRender: PropTypes.func,
  queryKey: PropTypes.string,
  service: PropTypes.object,
  value: PropTypes.any
};

SelectElementComponent.defaultProps = {
  filters: {},
  customMapItem: (v) => v
};
